import { useCallback, useState } from 'react';
import { getOrgLayout, updateOrgLayout } from 'src/api/organizationLayout';

import { getOfficeNameByType } from '../constants/officeImages';
import { OfficeType, SeatInfo } from '../types/seatTypes';
import { mergeSeatData, parseSeatChart, stringifySeatChart } from '../utils/seatChartParser';

interface UseOfficeLayoutReturn {
  seats: SeatInfo[];
  loading: boolean;
  error: string | null;
  loadSeats: (officeSid: number) => Promise<void>;
  updateSeat: (seat: SeatInfo) => void;
  saveSeats: (officeSid: number, officeType: OfficeType) => Promise<boolean>;
  resetSeats: () => void;
}

/**
 * 자리배치 데이터 관리 Hook
 */
export const useOfficeLayout = (): UseOfficeLayoutReturn => {
  const [seats, setSeats] = useState<SeatInfo[]>([]);
  const [originalSeats, setOriginalSeats] = useState<SeatInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 좌석 데이터 로드
   */
  const loadSeats = useCallback(async (officeSid: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getOrgLayout(officeSid);
      if (response?.data) {
        const parsedSeats = parseSeatChart(response.data.seatChart);
        setSeats(parsedSeats);
        setOriginalSeats(parsedSeats);
      } else {
        setSeats([]);
        setOriginalSeats([]);
      }
    } catch (err) {
      console.error('Failed to load seats:', err);
      setError('좌석 데이터를 불러오는데 실패했습니다.');
      setSeats([]);
      setOriginalSeats([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 단일 좌석 업데이트 (로컬 상태만)
   */
  const updateSeat = useCallback((seat: SeatInfo) => {
    setSeats((prevSeats) => mergeSeatData(prevSeats, seat));
  }, []);

  /**
   * 좌석 데이터 저장
   */
  const saveSeats = useCallback(
    async (officeSid: number, officeType: OfficeType): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const officeName = getOfficeNameByType(officeType);
        const seatChart = stringifySeatChart(seats);

        const response = await updateOrgLayout(officeSid, officeName, seatChart);

        if (response?.data) {
          setOriginalSeats(seats);
          return true;
        }

        setError('저장에 실패했습니다.');
        return false;
      } catch (err) {
        console.error('Failed to save seats:', err);
        setError('저장 중 오류가 발생했습니다.');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [seats],
  );

  /**
   * 좌석 데이터 초기화 (원래 상태로 복원)
   */
  const resetSeats = useCallback(() => {
    setSeats(originalSeats);
  }, [originalSeats]);

  return {
    seats,
    loading,
    error,
    loadSeats,
    updateSeat,
    saveSeats,
    resetSeats,
  };
};
